import {Box} from '@mui/material';
import type {LazyDataGridProps} from './types/LazyDataGridProps';
import './style/LazyDataGrid.scss';
import HeaderRow from './components/HeaderRow';
import {Row} from './components/Row';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DEFAULT_CACHED_ROW_SIZE,
  DEFAULT_FOOTER_HEIGHT,
  DEFAULT_PAGE_SIZE,
  DEFAULT_REQUEST_DEBOUNCE_TIME,
  DEFAULT_ROW_HEIGHT,
} from './constant';
import _ from 'lodash';
import type {LazyDataGridApi} from './types/LazyDataGridApi';

const PX_REM = 16;
const ROWS_RENDERED_TRIGGER_DELAY = 1000;

export function GenericLazyDataGrid<T>(
  props: LazyDataGridProps<T>,
  ref: React.Ref<LazyDataGridApi>
) {
  const {
    panelId,
    dataTestId = '',
    columns = [],
    dataSource,
    rowHeight = DEFAULT_ROW_HEIGHT,
    headerHeight,
    pageSize = DEFAULT_PAGE_SIZE,
    uniqueDataKey = 'id',
    dataRequestDebouceTime = DEFAULT_REQUEST_DEBOUNCE_TIME,
    showFooter = false,
    footerHeight = DEFAULT_FOOTER_HEIGHT,
    additionalRowCacheSize = DEFAULT_CACHED_ROW_SIZE,
    footer,
    getRowClass,
    onRowClick,
    onRowsRendered,
  } = props;

  const [rowsData, setRowsData] = useState<T[]>([]);
  const [totalRowCount, setTotalRowCount] = useState<number>(0);
  const [gridBodyHeight, setGridBodyHeight] = useState<number>(0);
  const [rowsSegmentTopPadding, setRowsSegmentTopPadding] = useState<number>(0);

  const gridHeaderContainerRef = useRef<HTMLDivElement>(null);
  const gridBodyRootRef = useRef<HTMLDivElement>(null);

  const gridHeaderRootRef = useRef<HTMLDivElement>(null);

  const [verticalScrollBarWidth, setVerticalScrollBarWidth] = useState(0);
  const [headerFullWidth, setHeaderFullWidth] = useState(0);
  const gridBodyTopVisiblePosition = useRef<number>(0);

  useImperativeHandle(ref, () => ({
    scrollToIndex: (rowIndex: number) => {
      scrollToPosition(rowIndex * rowHeight);
    },
    reloadData: () => handleScroll(true),
    getRow: (uniqueKey: string | number) => {
      return rowsData.find(
        (row: any) => row[uniqueDataKey.toString()] == uniqueKey
      ) as T;
    },
    updateRow: (row: T) => {
      setRowsData((prev) => {
        const updatedRows = prev.map((rowData: any) => {
          if (
            rowData[uniqueDataKey.toString()] ==
            (row as any)[uniqueDataKey.toString()]
          )
            return row;
          return rowData;
        });
        return [...updatedRows];
      });
    },
  }));
  const debouncedDisplayRowsData = useMemo(
    () =>
      _.debounce((topVisiblePosition: number) => {
        displayRowsData(topVisiblePosition);
      }, dataRequestDebouceTime),
    []
  );
  const handleScroll = (forceReload = false) => {
    const el = gridBodyRootRef.current;
    if (!el) return;
    const topVisiblePosition = el.scrollTop;
    if (
      forceReload ||
      rowsData.length == 0 ||
      Math.abs(topVisiblePosition - gridBodyTopVisiblePosition.current) >=
        (additionalRowCacheSize / 2) * rowHeight
    ) {
      gridBodyTopVisiblePosition.current = topVisiblePosition;
      debouncedDisplayRowsData(topVisiblePosition);
    }
  };
  const scrollToPosition = (position: number) => {
    const el = gridBodyRootRef.current;
    if (!el) return;
    el.scrollTop = position;
  };

  const getRowsData = async (startIndex: number, endIndex: number) => {
    const data = await dataSource?.getRows(startIndex, endIndex);
    return data;
  };

  const displayRowsData = async (startPosition: number) => {
    const topInvisibleRows = Math.floor(startPosition / rowHeight);
    let startIndex = 0;

    if (topInvisibleRows >= additionalRowCacheSize)
      startIndex = topInvisibleRows - additionalRowCacheSize;

    const endIndex = startIndex + pageSize;
    const rowsInfo = await getRowsData(startIndex, endIndex);
    setGridBodyHeight(rowsInfo.totalRowCount * rowHeight);
    if (totalRowCount != rowsInfo?.totalRowCount) {
      setTotalRowCount(rowsInfo?.totalRowCount ?? 0);
    }
    setRowsData(rowsInfo?.rows ?? []);
    setRowsSegmentTopPadding(startIndex * rowHeight);
    setTimeout(() => {
      onRowsRendered?.(rowsInfo?.rows ?? []);
    }, ROWS_RENDERED_TRIGGER_DELAY);
  };

  const pxToRem = (px: number) => px / PX_REM;

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (gridBodyRootRef.current) {
        setVerticalScrollBarWidth(
          gridBodyRootRef.current.offsetWidth -
            gridBodyRootRef.current.clientWidth
        );
      }
      if (gridHeaderContainerRef.current) {
        setHeaderFullWidth(gridHeaderContainerRef.current.clientWidth);
      }
    });
    if (gridBodyRootRef.current) {
      observer.observe(gridBodyRootRef.current);
    }
    if (gridHeaderContainerRef.current) {
      observer.observe(gridHeaderContainerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const header = gridHeaderRootRef.current;
    const body = gridBodyRootRef.current;

    if (!header || !body) return;

    const scrollSync = () => {
      header.scrollLeft = body.scrollLeft;
    };

    body.addEventListener('scroll', scrollSync);
    return () => {
      body.removeEventListener('scroll', scrollSync);
    };
  }, []);

  useEffect(() => {
    handleScroll();
  }, []);

  // Keyboard navigation
  // --------------------------
  const handleKeyDown = (ev: React.KeyboardEvent<HTMLElement>) => {
    const navKeys = [
      'ArrowDown',
      'ArrowUp',
      'ArrowLeft',
      'ArrowRight',
      'Enter',
    ];

    if (navKeys.includes(ev.key)) {
      const activeElm = document.activeElement as HTMLElement;
      const isRow =
        activeElm && activeElm.classList.contains('lazy-grid-row-container');
      const isCol = activeElm && activeElm.classList.contains('lazy-grid-cell');

      if (isRow || isCol) {
        ev.preventDefault();
        ev.stopPropagation();

        if (isRow && ev.key === 'Enter') {
          const rowIndex =
            activeElm
              ?.closest('.lazy-grid-row-container')
              ?.getAttribute('data-row-index') ?? null;
          if (rowIndex && onRowClick) {
            onRowClick(rowsData[parseInt(rowIndex)]);
          }
          return;
        }

        if (isCol) {
          if (ev.key === 'ArrowRight') {
            (activeElm.nextSibling as HTMLElement)?.focus();
          }
          if (ev.key === 'ArrowLeft') {
            (activeElm.previousSibling as HTMLElement)?.focus();
          }
        }

        if (ev.key === 'ArrowDown') {
          (activeElm.nextSibling as HTMLElement)?.focus();
        }
        if (ev.key === 'ArrowUp') {
          (activeElm.previousSibling as HTMLElement)?.focus();
        }
      }
    }
  };
  // --------------

  return (
    <Box
      className="lazy-data-grid"
      data-testid={dataTestId}
      role="grid"
      aria-colcount={columns?.length || 0}
      aria-rowcount={rowsData?.length || 0}
      onKeyDown={handleKeyDown}
    >
      <Box className="lazy-data-grid-root">
        <Box className="lazy-data-grid-content-root">
          <Box ref={gridHeaderRootRef} className="lazy-data-grid-header-root">
            <Box display="flex">
              <HeaderRow
                columns={columns}
                ref={gridHeaderContainerRef}
                headerHeight={headerHeight}
              />
              <Box
                className="verticla-scrollbar-header-space"
                component="span"
                minWidth={`${pxToRem(verticalScrollBarWidth)}rem`}
                width={`${pxToRem(verticalScrollBarWidth)}rem`}
              ></Box>
            </Box>
          </Box>

          <Box
            className="lazy-data-grid-body-root"
            ref={gridBodyRootRef}
            onScroll={() => handleScroll()}
          >
            <Box
              height={`${pxToRem(gridBodyHeight)}rem`}
              width={
                rowsData.length ? '100%' : `${pxToRem(headerFullWidth)}rem`
              }
              className="body-background-panel"
            >
              <Box paddingTop={`${pxToRem(rowsSegmentTopPadding)}rem`}>
                {rowsData.map((row: any, index: number) => (
                  <Row
                    key={row[uniqueDataKey]}
                    dataTestId={`${dataTestId}-row-${index}`}
                    row={row}
                    rowIndex={index}
                    columns={columns}
                    rowHeight={rowHeight}
                    onRowClick={(row) => onRowClick?.(row)}
                    getRowClass={(row) => getRowClass?.(row) ?? ''}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
        {showFooter && (
          <Box
            className="lazy-data-grid-footer-root"
            sx={{
              minHeight: `${pxToRem(footerHeight)}rem`,
              maxHeight: `${pxToRem(footerHeight)}rem`,
              height: `${pxToRem(footerHeight)}rem`,
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export const LazyDataGrid = forwardRef(GenericLazyDataGrid) as <T>(
  props: LazyDataGridProps<T> & {
    ref?: React.Ref<LazyDataGridApi>;
  }
) => React.ReactElement;
